import React, { useEffect, useMemo, useState } from 'react';
import Cal, { getCalApi } from '@calcom/embed-react';
import { FormField, SchedulerOptions } from '../../types/form';
import { CalendarClock, CheckCircle2 } from 'lucide-react';

interface SchedulerFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  /** Prefill the Cal.com widget so the respondent doesn't re-enter what they
   *  already typed earlier in the form. */
  prefillName?: string;
  prefillEmail?: string;
}

// Rhety palette
const INK = '#26251e';
const ACCENT = '#f54e00';
const PAPER = '#faf9f6';

// Normalize a full URL or bare slug into Cal's calLink form ("user/event").
const toCalLink = (raw: string): string => {
  const trimmed = (raw || '').trim();
  const m = trimmed.match(/^https?:\/\/(?:app\.)?cal\.com\/(.+)$/i);
  return (m ? m[1] : trimmed).replace(/^\/+/, '').replace(/\/+$/, '').split('?')[0];
};

// A stable namespace per calLink (getCalApi + <Cal> must share it).
const toNamespace = (calLink: string): string =>
  calLink.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'rhety';

const SchedulerField: React.FC<SchedulerFieldProps> = ({
  field,
  value,
  onChange,
  prefillName,
  prefillEmail,
}) => {
  const options = (field.options || {}) as SchedulerOptions;
  const calLink = useMemo(() => toCalLink(options.calLink || ''), [options.calLink]);
  const namespace = useMemo(() => toNamespace(calLink), [calLink]);

  // Cal.com prefill: name + email flow through to the booking form as query
  // params, so the respondent doesn't re-type what they already entered.
  const calConfig = useMemo(() => {
    const cfg: Record<string, string> = {
      layout: 'month_view',
      useSlotsViewOnSmallScreen: 'true',
    };
    if (prefillName && prefillName.trim()) cfg.name = prefillName.trim();
    if (prefillEmail && prefillEmail.trim()) cfg.email = prefillEmail.trim();
    return cfg;
  }, [prefillName, prefillEmail]);

  const [booked, setBooked] = useState<boolean>(!!value?.booked);
  useEffect(() => {
    setBooked(!!value?.booked);
  }, [value?.booked]);

  useEffect(() => {
    if (!calLink) return;
    let cancelled = false;

    (async () => {
      const cal = await getCalApi({ namespace });
      if (cancelled) return;
      cal('ui', {
        cssVarsPerTheme: {
          light: { 'cal-brand': ACCENT },
          dark: { 'cal-brand': ACCENT },
        },
        hideEventTypeDetails: false,
        layout: 'month_view',
      });
      cal('on', {
        action: 'bookingSuccessful',
        callback: (e: any) => {
          const detail = e?.detail?.data || e?.detail || {};
          const booking = detail.booking || detail.confirmedBooking || detail;
          setBooked(true);
          onChange({
            booked: true,
            startTime: booking?.startTime || booking?.start || detail?.date || undefined,
            bookingUid: booking?.uid || booking?.id || detail?.uid || undefined,
            calLink,
            timestamp: new Date().toISOString(),
          });
        },
      });
    })();

    return () => {
      cancelled = true;
    };
    // onChange intentionally omitted — it's captured per render and the effect
    // only needs to re-run if the booking target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calLink, namespace]);

  if (!calLink) {
    return (
      <div
        className="rounded-xl border p-4"
        style={{ borderColor: `${INK}1f`, backgroundColor: PAPER, color: INK }}
      >
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5" style={{ color: ACCENT }} aria-hidden="true" />
          <span className="font-medium" style={{ color: INK }}>
            Scheduling not configured
          </span>
        </div>
        <p className="mt-1 text-sm" style={{ color: `${INK}99` }}>
          A booking link has not been set up for this step yet. Please contact the form owner.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: `${INK}1f`, backgroundColor: PAPER, color: INK }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5" style={{ color: ACCENT }} aria-hidden="true" />
          <span className="font-medium" style={{ color: INK }}>
            Book your session
          </span>
        </div>
        {booked && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={{ backgroundColor: '#16653414', color: '#166534' }}
          >
            <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
            Booked
          </span>
        )}
      </div>

      <div
        className="mt-3 overflow-hidden rounded-lg border"
        style={{ borderColor: `${INK}14`, backgroundColor: '#ffffff' }}
      >
        <Cal
          namespace={namespace}
          calLink={calLink}
          // Let the embed auto-size to its content. Pinning a fixed height with
          // overflow:scroll traps the month-view time-slot column inside a 640px
          // box whose inner scroll anchors to the bottom — so the earliest times
          // (before ~11:00) end up above the scroll area with no way to reach them.
          style={{ width: '100%', minHeight: '640px', overflow: 'visible' }}
          config={calConfig}
        />
      </div>

      {booked && (
        <p className="mt-2 text-sm" style={{ color: '#166534' }}>
          Your booking is confirmed
          {value?.startTime ? ` for ${new Date(value.startTime).toLocaleString()}` : ''}.
        </p>
      )}
    </div>
  );
};

export default SchedulerField;
